import * as React from 'react';
import { ComponentType } from 'react';
import Select, {
  ValueContainerProps,
  components as RSComponents,
} from 'react-select';
import Creatable, { Props as RSProps } from 'react-select/creatable';
import { IndicatorProps } from 'react-select/src/components/indicators';
import { Blank, SELECT_STYLE } from '../constants';
import { IndicatorComponentType } from 'react-select/src/components';
import { OptionType } from '../types';

type CustomProps = {
  isCreatable?: boolean;
  showDropdownArrow?: boolean;
  isMultiEllipsis?: boolean;
};

// RSProps represents props of Creatable, which is an superset of props of Select
// with every additional property marked as optional
export type Props<OT extends OptionType> = RSProps<OT> & CustomProps;

export default class InputDropdown<
  OT extends OptionType = OptionType,
> extends React.PureComponent<Props<OT>> {
  render() {
    const {
      isCreatable = false,
      showDropdownArrow = false,
      isMultiEllipsis = false,
      styles = {},
      components,
      ...rest
    } = this.props;
    const Dropdown: React.ComponentType<RSProps<OT>> = isCreatable
      ? Creatable
      : Select;
    const selectComponents = components || {};
    selectComponents.IndicatorSeparator = Blank;
    if (!showDropdownArrow) {
      selectComponents.DropdownIndicator = Blank;
    } else {
      // type assertion: RSComponents.DropdownIndicator is nullable as it's an optional in "components" prop,
      // here we're manually importing it, so it is not nullable
      const RSDropdownIndicator =
        RSComponents.DropdownIndicator as IndicatorComponentType<OT>;

      // add a class onto the svg for custom styling
      selectComponents.DropdownIndicator = (props: IndicatorProps<OT>) => (
        <RSDropdownIndicator {...props}>
          <RSComponents.DownChevron className="react-select-dropdown-chevron" />
        </RSDropdownIndicator>
      );
    }
    let multiEllipsisStyle = {};
    if (isMultiEllipsis) {
      rest.isMulti = true;
      rest.hideSelectedOptions = false;

      multiEllipsisStyle = {
        valueContainer: (base: any) => ({
          ...base,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }),
      };

      const RSValueContainer = RSComponents.ValueContainer as ComponentType<
        ValueContainerProps<OT>
      >;

      selectComponents.ValueContainer = ({
        selectProps,
        children,
        ...props
      }: ValueContainerProps<any>) => {
        let [values, input] = children as any;
        if (Array.isArray(values)) {
          values = selectProps?.value?.map((x: any) => x.label).join(', ');
        }

        return (
          <RSValueContainer selectProps={selectProps} {...props}>
            <div
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {values}
            </div>
            {input}
          </RSValueContainer>
        );
      };
    }

    return (
      <Dropdown
        styles={{
          ...SELECT_STYLE,
          ...styles,
          ...multiEllipsisStyle,
        }}
        components={selectComponents}
        blurInputOnSelect={isMultiEllipsis}
        {...rest}
      />
    );
  }
}
